import api from '@/api';
import * as types from '@/store/types';
import get from 'lodash/get';

const initialState = {
    id: null,
    isAdmin: false,
    isSuperAdmin: false,
    isSupport: false,
    email: null,
    name: null,
    firstname: null,
    lastname: null,
    messenger: null,
    defaultCurrency: null,
    privileges: {},
    subscription: {},
    chart: {},
    roles: [],
    timeframe: null,
};

const state = { ...initialState };

const getters = {
    isCurrentUserAdmin: state => state.isAdmin,
    isCurrentUserSuperAdmin: state => state.isSuperAdmin,
    isCurrentUserSupport: state => state.isSupport,
    isCurrentUserAdvertiserManager: state => state.isAdvertiserManager,
    currentUserPrivilegesChanged: state => state.privilegesChanged,
    currentUserId: state => state.id,
    currentUserHasModulePrivileges: state => (permissionsModuleName) => {
        const isPrivilege = !!get(state.privileges, permissionsModuleName);

        return isPrivilege;
    },
    currentUserSubscriptionCost: state => state.subscription,
    currentUserSubscriptionChart: state => state.chart,
    currentUserTimeframe: state => state.timeframe?.data,
    currentUserEmail: state => state.email,
};

const actions = {
    getCurrentUser ({ dispatch, commit }) {
        const userProfile = api.profile.list()
            .then(({ data }) => {
                data = data.data;
                data.isAdmin = data.roles && data.roles.includes('admin');
                data.isSuperAdmin = data.roles && data.roles.includes('super_user');
                data.isSupport = data.isSuperAdmin;
                data.isAdvertiserManager = data.roles
                    && data.roles.some(role => ['advertiser_manager', 'advertiser_manager_read_only'].includes(role));
                commit(types.RECEIVE_CURRENT_USER, data);
                dispatch('populateCurrentUserPrivileges');

                return data;
            });

        const userDashboardTimeframe = dispatch('getCurrentUserTimeframe');

        return Promise.all([ userProfile, userDashboardTimeframe ]);
    },

    getCurrentUserSubscriptionChart ({ commit }, params) {
        return api.subscription.chart(params)
            .then(({ data }) => {
                commit(types.RECEIVE_SUBSCRIPTION_COST_CHART, { type: params.type, ...data.data } );
            });
    },

    getCurrentUserSubscriptionInfo ({ commit }) {
        return api.subscription.info()
            .then(({ data }) => {
                commit(types.RECEIVES_SUBSCRIPTION_INFO, data.data);
            });
    },

    getCurrentUserStorageItems (store, params) {
        return api.profile.getStorageItems(params)
            .then(({ data }) => ({
                data: data.data.map(item => ({ ...item })),
                meta: data.meta,
            }));
    },

    getCurrentUserStorageItem (store, id) {
        return id && api.profile.getStorageItemById(id);
    },

    getCurrentUserTimeframe ({ dispatch, commit }, params) {
        return dispatch('getCurrentUserStorageItems', { type: 'dashboard:timeframe', limit: 1, sort: '-created', ...params })
            .then(({ data } = {}) => dispatch('getCurrentUserStorageItem', data?.[0]?.id))
            .then(({ data } = {}) => commit(types.RECEIVE_CURRENT_USER_TIMEFRAME, data?.data))
            .catch((error) => console.error(error)); // eslint-disable-line no-console
    },

    updateCurrentUserTimeframe ({ state, commit }, payload) {
        const dashboardTimeframeStorageId = state?.timeframe?.id;
        if (dashboardTimeframeStorageId) {
            return api.profile.updateStorageItem(`${ dashboardTimeframeStorageId }`, { name: 'dashboard:timeframe', data: payload })
                .then(({ data }) => commit(types.RECEIVE_CURRENT_USER_TIMEFRAME, { id: data?.data?.id, data: payload }));
        } else {
            return api.profile.postStorageItem({
                name: 'dashboard:timeframe',
                type: 'dashboard:timeframe',
                data: payload,
            })
                .then(({ data }) => commit(types.RECEIVE_CURRENT_USER_TIMEFRAME, { id: data?.data?.id, data: payload }));
        }
    },
};

const mutations = {
    [types.RECEIVE_CURRENT_USER] (state, data) {
        Object.assign(state, data);
    },
    [types.RESET_CURRENT_USER] (state) {
        Object.assign(state, initialState);
    },
    [types.RECEIVES_SUBSCRIPTION_INFO] (state, data) {
        state.subscription = data;
    },
    [types.RECEIVE_SUBSCRIPTION_COST_CHART] (state, { type = 'total_cost', ...data }) {
        state.chart = { ...state.chart, [type]: data };
    },
    [types.RECEIVE_CURRENT_USER_TIMEFRAME] (state, data) {
        if (data) {
            state.timeframe = data;
        }
    },
};

export default {
    getters,
    state,
    actions,
    mutations,
};
